import React, { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

import { PrimaryButton, SecondaryButton, TertiaryButton } from './button/Button';
import { isStaleChunkError, reloadOnceForStaleChunk } from '../utils/lazyWithRetry.js';
import { performSessionCleanup, safeRedirectToLogin } from '../authentication/sessionCleanup.js';

import '../pages/error-pages/errorPages.css';

/**
 * The page any route falls back to when it throws (routes/index.jsx puts it on every top-level route). Without it the
 * router shows its own "Unexpected Application Error!" - a dead end. A stale chunk after a deploy reloads once, as
 * the lazy loader does; anything else says so and offers a way out: reload, go home, or sign in again.
 */
export const ErrorCard = ({ error }) => {
    const staleChunk = isStaleChunkError(error);

    useEffect(() => {
        if (staleChunk) reloadOnceForStaleChunk();
    }, [staleChunk]);

    const signInAgain = async () => {
        await performSessionCleanup();
        safeRedirectToLogin();
    };

    if (staleChunk) {
        return (
            <div className="error-page">
                <div className="error-card">
                    <div className="error-icon" aria-hidden="true">
                        <FontAwesomeIcon icon={faArrowsRotate} />
                    </div>
                    <h1>Update Available</h1>
                    <p>A new version of the app has been deployed.</p>
                    <p className="error-subtle">Please reload the page to continue.</p>
                    <div className="error-actions">
                        <PrimaryButton onClick={() => window.location.reload()}>Reload Page</PrimaryButton>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="error-page">
            <div className="error-card">
                <div className="error-icon error-icon--error" aria-hidden="true">
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                </div>
                <h1>Something Went Wrong</h1>
                <p>This page hit a problem it couldn't recover from.</p>
                <p className="error-subtle">Reloading usually fixes it. If it keeps happening, go to the home page or sign in again.</p>
                <div className="error-actions">
                    <PrimaryButton onClick={() => window.location.reload()}>Reload Page</PrimaryButton>
                    <SecondaryButton onClick={() => window.location.assign('/home')}>Go to Home Page</SecondaryButton>
                    <TertiaryButton onClick={signInAgain}>Sign In Again</TertiaryButton>
                </div>
            </div>
        </div>
    );
};

const RouteErrorPage = () => {
    const error = useRouteError();
    useEffect(() => {
        console.error('Route error:', error);
    }, [error]);
    return <ErrorCard error={error} />;
};

export default RouteErrorPage;
